import { FC, useCallback, useState } from 'react';
import {
  Alert,
  Button,
  Image,
  Input,
  Space,
  Switch,
  Typography,
  message
} from 'antd';
import { useTranslation } from 'react-i18next';
import { parseJson } from '@medicusunion/front-utils';
import { useAppSelector } from 'store/hooks';
import { selectUserData } from 'store/user/selectors';
import { useAuthDispatchContext } from 'providers/Auth';
import {
  disable2fa,
  generateAuthQr,
  regenerateRecoveryCodes,
  verify2fa
} from 'services/api/auth';

import { showErrorMessage } from 'utils/showErrorMessage';
import { StyledList, StyledToken } from './styled';
import RecoveryCodes from 'components/shared/RecoveryCodes';
import TwoFaCodeModal from 'components/shared/TwoFaCodeModal';

const { Title, Paragraph } = Typography;

type Prompt = 'disable' | 'regenerate' | null;

const Authentication: FC = () => {
  const { t } = useTranslation();
  const { refetchUserData } = useAuthDispatchContext();
  const user = useAppSelector(selectUserData());

  const settings = parseJson(user?.settings) as Record<string, unknown> | null;

  const [code, setCode] = useState('');
  const [error, setError] = useState(false);
  const [active, setActive] = useState(settings?.auth2fa === true);
  const [prompt, setPrompt] = useState<Prompt>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  const [result, setResult] = useState<{
    qr: string;
    secretCode: string;
  } | null>(null);

  // Stamped by the backend only on accounts whose 2FA was actually
  // stood down by the seed migration. Read-only, and cleared
  // server-side on the next activation, so the notice clears itself.
  const mustRePair = settings?.auth2fa !== true && !!settings?.auth2fa_reset_at;

  const onConfirm = useCallback(async () => {
    if (!code) {
      setError(true);

      return;
    }

    try {
      // POST /auth/2fa/verify is what ACTIVATES 2FA now. The old
      // settings write that followed it is ignored by the backend, and
      // this is the only place the recovery codes are returned.
      const data = await verify2fa({ otp: code });

      await refetchUserData();
      setResult(null);
      setCode('');
      setActive(true);

      if (data?.recoveryCodes?.length) {
        setRecoveryCodes(data.recoveryCodes);

        return;
      }

      message.success(t('profile.2fa.success'));
    } catch (err) {
      setError(true);
      showErrorMessage(err);
    }
  }, [code, refetchUserData, t]);

  const onToggleSwitch = useCallback(async (val: boolean) => {
    if (val) {
      try {
        const qr = await generateAuthQr();

        setResult({ qr: qr.qr, secretCode: qr.secretKey });
        setActive(true);
      } catch (err) {
        showErrorMessage(err);
      }

      return;
    }

    // Turning 2FA off needs a live code now: a session alone is not
    // enough, so a stolen session cannot strip the second factor.
    setPrompt('disable');
  }, []);

  const onDisable = useCallback(
    async (otp: string) => {
      await disable2fa({ otp });
      await refetchUserData();
      setPrompt(null);
      setResult(null);
      setActive(false);
      message.success(t('profile.2fa.disabled'));
    },
    [refetchUserData, t]
  );

  const onRegenerate = useCallback(async (otp: string) => {
    const data = await regenerateRecoveryCodes({ otp });

    setPrompt(null);
    setRecoveryCodes(data.recoveryCodes);
  }, []);

  if (recoveryCodes) {
    return (
      <RecoveryCodes
        codes={recoveryCodes}
        onDone={() => setRecoveryCodes(null)}
      />
    );
  }

  return (
    <Space direction="vertical" size={16} style={{ maxWidth: '100%' }}>
      {mustRePair && (
        <Alert
          type="warning"
          showIcon
          message={t('profile.2fa.reset_notice')}
          data-test-id="2fa-reset-notice"
        />
      )}

      <Space>
        <Switch checked={active} onChange={onToggleSwitch} />
        <Typography.Text>{t('profile.2fa.title')}</Typography.Text>
      </Space>

      {active && !result && (
        <Button
          onClick={() => setPrompt('regenerate')}
          data-test-id="regenerate-recovery-codes-button"
        >
          {t('profile.2fa.recovery.regenerate')}
        </Button>
      )}

      {!!result && (
        <>
          <div>
            <Title level={4}>{t('profile.2fa.configuring')}</Title>
            <StyledList>
              <li>{t('profile.2fa.install_app')}</li>
              <li>{t('profile.2fa.add')}</li>
              <li>{t('profile.2fa.select_scan')}</li>
            </StyledList>
          </div>
          <div>
            <Title level={4}>{t('profile.2fa.scan_code')}</Title>
            <Image width={200} height={200} preview={false} src={result.qr} />
          </div>
          <div>
            <Title level={4}>{t('profile.2fa.enter_code')}</Title>
            <StyledToken copyable code>
              {result.secretCode}
            </StyledToken>
          </div>
          <div>
            <Title level={4}>{t('profile.2fa.verify_code')}</Title>
            <Paragraph>{t('profile.2fa.verify')}</Paragraph>
            <Input
              value={code}
              maxLength={6}
              onChange={e => {
                setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                setError(false);
              }}
              status={error ? 'error' : ''}
            />
          </div>
          <Button onClick={onConfirm} block type="primary">
            {t('profile.2fa.confirm')}
          </Button>
        </>
      )}

      <TwoFaCodeModal
        open={prompt === 'disable'}
        title={t('profile.2fa.disable_title')}
        description={t('profile.2fa.disable_description')}
        okText={t('profile.2fa.disable_submit')}
        allowRecoveryCode
        onSubmit={onDisable}
        onClose={() => {
          setPrompt(null);
          // The switch was flicked off optimistically; 2FA is still on
          // until the backend says otherwise.
          setActive(settings?.auth2fa === true);
        }}
      />

      <TwoFaCodeModal
        open={prompt === 'regenerate'}
        title={t('profile.2fa.recovery.regenerate')}
        description={t('profile.2fa.recovery.regenerate_description')}
        okText={t('profile.2fa.confirm')}
        onSubmit={onRegenerate}
        onClose={() => setPrompt(null)}
      />
    </Space>
  );
};

export default Authentication;
